import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck, Zap, Download, Infinity as InfinityIcon, RefreshCw } from 'lucide-react';
type ModalType = 'terms' | 'privacy' | 'contact' | null;
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-255" data-mp="3uesb1j9yv7im">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-256" data-mp="3uftkxkf8xnyr">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-257" data-mp="29y5l8cy0g70t" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25
      }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-258" data-mp="2eyt7ymf7i1p8">
            <div className="flex items-center justify-between p-6 border-b border-brand-beige" data-id="element-259" data-mp="34gwrh0rwr7l">
              <h3 className="text-xl md:text-2xl font-bold text-brand-green font-serif" data-id="element-260" data-mp="hsk0skv99str">
                {title}
              </h3>
              <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full bg-brand-beige hover:bg-brand-beige/80 transition-colors" data-id="element-261" data-mp="cv1smwp2bhmt">
                <X className="w-5 h-5 text-brand-text" data-id="element-262" data-mp="mthlb43qmyyf" />
              </button>
            </div>
            <div className="p-6 md:p-8 overflow-y-auto max-h-[calc(85vh-80px)] text-brand-text/80 text-sm md:text-base leading-relaxed space-y-4" data-id="element-263" data-mp="two4hv2q1j7u">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-brand-green text-white relative overflow-hidden" data-id="element-264" data-mp="1ckkp4swnx2n0">
        {/* Decorative background */}
        <div className="absolute inset-0 overflow-hidden opacity-10 pointer-events-none" data-id="element-265" data-mp="1hj4aar3fmaeb">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-brand-gold rounded-full blur-3xl" data-id="element-266" data-mp="1ml3eg36zxbcb"></div>
          <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] bg-brand-gold rounded-full blur-3xl" data-id="element-267" data-mp="1ri3mukrbiq6l"></div>
        </div>

        {/* Closing quote */}
        <div className="relative z-10 border-b border-white/10" data-id="element-268" data-mp="3t9gew986t3l8">
          <div className="max-w-4xl mx-auto px-6 py-12 md:py-14 text-center" data-id="element-269" data-mp="2af3r7lrdkvhi">
            <p className="font-serif text-xl sm:text-2xl md:text-3xl text-white/90 leading-snug italic tracking-tight" data-id="element-270" data-mp="2ah858wiznavi">
              &ldquo;No es solo vender online. Es construir una{' '}
              <span className="text-brand-gold not-italic font-semibold" data-id="element-271" data-mp="21n1unykffzci">
                oportunidad real
              </span>{' '}
              para tu futuro.&rdquo;
            </p>
          </div>
        </div>

        {/* Main footer content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-16 md:py-20" data-id="element-272" data-mp="2pbuuw7830tcx">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-10 text-center md:text-left" data-id="element-273" data-mp="2kdb6m060n5he">
            {/* Block 1: Brand */}
            <div data-id="element-274" data-mp="2fd5ao7spqwti">
              <h4 className="font-serif text-lg font-bold tracking-wide mb-3 text-brand-gold" data-id="element-275" data-mp="2fdos7negbj70">
                ECOSISTEMA ECOMMERCE
              </h4>
              <p className="text-white/55 text-sm leading-relaxed max-w-xs mx-auto md:mx-0" data-id="element-276" data-mp="13zb7grezvjv7">
                Aprende Ecommerce moderno y Amazon con sistemas prácticos
                diseñados para ayudarte a construir ingresos online reales.
              </p>
            </div>

            {/* Block 2: Legal links */}
            <div data-id="element-277" data-mp="2ivuet29s35f0">
              <h5 className="text-xs font-bold uppercase tracking-[0.18em] text-brand-gold mb-4" data-id="element-278" data-mp="2iwcgwfj4gduu">
                Legal
              </h5>
              <div className="flex flex-col gap-3" data-id="element-279" data-mp="2nrrt731he1p5">
                <button onClick={() => setActiveModal('terms')} className="text-white/60 text-sm hover:text-brand-gold transition-colors text-center md:text-left" data-id="element-280" data-mp="3bkrt6uavci2h">
                  Términos y Condiciones
                </button>
                <button onClick={() => setActiveModal('privacy')} className="text-white/60 text-sm hover:text-brand-gold transition-colors text-center md:text-left" data-id="element-281" data-mp="36juggx8mog8d">
                  Política de Privacidad
                </button>
              </div>
            </div>

            {/* Block 3: Trust */}
            <div data-id="element-282" data-mp="1nnsgvxmzc15d">
              <h5 className="text-xs font-bold uppercase tracking-[0.18em] text-brand-gold mb-4" data-id="element-283" data-mp="1in4r1fahlqcq">
                Tu acceso
              </h5>
              <div className="flex flex-col gap-3" data-id="element-284" data-mp="1wlcl9fg52dk7">
                <span className="flex items-center justify-center md:justify-start gap-2 text-white/65 text-sm" data-id="element-285" data-mp="1rnjbydjjh3fp">
                  <ShieldCheck className="w-4 h-4 text-brand-gold flex-shrink-0" data-id="element-286" data-mp="13xgqwhe1kxql" />
                  Compra Segura
                </span>
                <span className="flex items-center justify-center md:justify-start gap-2 text-white/65 text-sm" data-id="element-287" data-mp="zxy41g2nucl4">
                  <Zap className="w-4 h-4 text-brand-gold flex-shrink-0" data-id="element-288" data-mp="1dp2xu1jckanj" />
                  Acceso Inmediato
                </span>
                <span className="flex items-center justify-center md:justify-start gap-2 text-white/65 text-sm" data-id="element-289" data-mp="l77d0a8w7hj2">
                  <Download className="w-4 h-4 text-brand-gold flex-shrink-0" data-id="element-290" data-mp="g8xj02ywmmz1" />
                  Descarga Digital
                </span>
                <span className="flex items-center justify-center md:justify-start gap-2 text-white/65 text-sm" data-id="element-291" data-mp="2ga41phklul1j">
                  <InfinityIcon className="w-4 h-4 text-brand-gold flex-shrink-0" data-id="element-292" data-mp="2l7vpq0i4pn0g" />
                  Acceso de por vida
                </span>
                <span className="flex items-center justify-center md:justify-start gap-2 text-white/65 text-sm" data-id="element-293" data-mp="2v01k1gvusgit">
                  <RefreshCw className="w-4 h-4 text-brand-gold flex-shrink-0" data-id="element-294" data-mp="1xckrwbqboye2" />
                  Actualizaciones gratuitas
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Digital products legal note */}
        <div className="relative z-10 border-t border-white/10" data-id="element-295" data-mp="27cbk26rev1az">
          <div className="max-w-4xl mx-auto px-6 py-8 md:py-10" data-id="element-296" data-mp="2bd3v95jufg9w">
            <div className="bg-white/5 border border-white/10 rounded-2xl px-6 py-6" data-id="element-297" data-mp="3ipnke7nyp1se">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-gold mb-3 text-center" data-id="element-298" data-mp="3mqfs2wh6qymk">
                Política de productos digitales
              </p>
              <p className="text-white/55 text-xs sm:text-[13px] leading-relaxed mb-3" data-id="element-299" data-mp="1b56ucyzzvvyu">
                Al realizar esta compra, el cliente reconoce que está
                adquiriendo un producto digital descargable de acceso inmediato.
                Debido a la naturaleza intangible del contenido digital, todas
                las ventas son finales y no aplican devoluciones ni reembolsos
                una vez enviado el acceso al producto.
              </p>
              <p className="text-white/45 text-xs sm:text-[13px] leading-relaxed" data-id="element-300" data-mp="nc8a7b6wyvjp">
                Digital Products NC se compromete únicamente a garantizar la
                correcta entrega del contenido adquirido y la asistencia en caso
                de problemas técnicos relacionados con la descarga o recepción
                del material.
              </p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="relative z-10 border-t border-white/10" data-id="element-301" data-mp="1hls32hm44e8g">
          <div className="max-w-6xl mx-auto px-6 py-6 text-center" data-id="element-302" data-mp="1hmk0zz36mo1w">
            {/* Copyright */}
            <p className="text-white/40 text-xs mb-3" data-id="element-303" data-mp="1mk20yzz81sxr">
              © {new Date().getFullYear()} Ecosistema Ecommerce. Todos los
              derechos reservados.
            </p>
            {/* Disclaimer */}
            <p className="text-white/25 text-[10px] max-w-2xl mx-auto leading-relaxed" data-id="element-304" data-mp="1csyzvy6aldyh">
              Este sitio no es parte del sitio web de Facebook o Facebook Inc.
              Además, este sitio NO está respaldado por Facebook de ninguna
              manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
            </p>
          </div>
        </div>
      </footer>

      {/* Terms Modal */}
      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-305" data-mp="tyac2oao9k6j">
        <p className="font-semibold text-brand-text" data-id="element-306" data-mp="2oagxefxa5hck">
          Última actualización: {new Date().getFullYear()}
        </p>
        <p data-id="element-307" data-mp="cndd6zp66ein">
          Al adquirir cualquier producto digital ofrecido a través de este sitio
          web, aceptas los siguientes términos y condiciones:
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-308" data-mp="mcwxm9sj6gfm">
          1. Naturaleza del Producto
        </h4>
        <p data-id="element-309" data-mp="hloqxlil5965">
          Todos los productos ofrecidos son de naturaleza digital (ebooks,
          plantillas, checklists). Una vez completada la compra, recibirás
          acceso inmediato al contenido a través de descarga digital.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-310" data-mp="qgcth3ws0cr9">2. Uso Personal</h4>
        <p data-id="element-311" data-mp="15b0ua4r8en0j">
          El contenido adquirido es para uso personal y educativo únicamente.
          Queda estrictamente prohibida la redistribución, reventa, copia o
          compartición del material con terceros sin autorización expresa por
          escrito.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-312" data-mp="324kvc7fdcczh">
          3. Política de No Reembolso
        </h4>
        <p data-id="element-313" data-mp="iv9vsu3sc3di">
          Debido a la naturaleza digital e inmediata del producto, no se ofrecen
          reembolsos una vez que el contenido ha sido entregado y/o descargado.
          Al realizar la compra, aceptas esta condición.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-314" data-mp="8y5bz0xewjzv">
          4. Propiedad Intelectual
        </h4>
        <p data-id="element-315" data-mp="3zc7tz40m5cd">
          Todo el contenido, diseño, textos, imágenes y materiales están
          protegidos por derechos de autor. La propiedad intelectual pertenece
          exclusivamente a Sistema de Ingreso Desde Casa.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-316" data-mp="ruey8bigwguy">5. Modificaciones</h4>
        <p data-id="element-317" data-mp="mvtx89ve5t5g">
          Nos reservamos el derecho de modificar el contenido, precios, ofertas
          y estos términos en cualquier momento sin previo aviso. Las compras
          realizadas antes de cualquier cambio mantienen las condiciones
          vigentes al momento de la transacción.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-318" data-mp="1ntewgqnibzyo">6. Resultados</h4>
        <p data-id="element-319" data-mp="1nv8345y4ek6o">
          Los resultados pueden variar según la dedicación, esfuerzo y
          circunstancias individuales de cada persona. No garantizamos ingresos
          específicos ni resultados financieros.
        </p>
      </Modal>

      {/* Privacy Modal */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-320" data-mp="21tpohx0jzklf">
        <p className="font-semibold text-brand-text" data-id="element-321" data-mp="2atl4cf5203rs">
          Última actualización: {new Date().getFullYear()}
        </p>
        <p data-id="element-322" data-mp="2fk1iy2km1vfq">
          Tu privacidad es importante para nosotros. Esta política describe cómo
          recopilamos, usamos y protegemos tu información personal.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-323" data-mp="2pihdkfiujmtz">
          1. Información que Recopilamos
        </h4>
        <p data-id="element-324" data-mp="2uhc2lz7eq7mj">
          Recopilamos información que proporcionas voluntariamente al realizar
          una compra, como tu nombre, dirección de correo electrónico e
          información de pago. Esta información es necesaria para procesar tu
          pedido y entregar el producto.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-325" data-mp="2eizoj9vp5pza">
          2. Uso de la Información
        </h4>
        <p data-id="element-326" data-mp="29ssz57td21fz">Tu información se utiliza exclusivamente para:</p>
        <ul className="list-disc pl-6 space-y-1" data-id="element-327" data-mp="29qgakbt3ksty">
          <li data-id="element-328" data-mp="2nj4wpm8pssei">Procesar y entregar tu compra</li>
          <li data-id="element-329" data-mp="2nm0tv8cqnd1w">Enviarte el producto adquirido</li>
          <li data-id="element-330" data-mp="2ilmjvkrktt18">Comunicarnos contigo sobre tu pedido</li>
          <li data-id="element-331" data-mp="2imxn9jh40yrt">
            Enviarte información relevante sobre actualizaciones del producto
            (si lo autorizas)
          </li>
        </ul>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-332" data-mp="2sir61klf9oi1">
          3. Protección de Datos
        </h4>
        <p data-id="element-333" data-mp="13549s28pfj8l">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información personal contra acceso no autorizado, pérdida
          o alteración.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-334" data-mp="39teddh68eey4">
          4. No Venta de Datos
        </h4>
        <p data-id="element-335" data-mp="3eqem5zugtvsx">
          No vendemos, alquilamos ni compartimos tu información personal con
          terceros con fines comerciales. Tu información es confidencial y está
          protegida.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-336" data-mp="350bt5zncghul">
          5. Cookies y Analíticas
        </h4>
        <p data-id="element-337" data-mp="2r12ex5ip7xt9">
          Este sitio puede utilizar cookies y herramientas de analítica web para
          mejorar la experiencia del usuario y analizar el tráfico. Estas
          herramientas recopilan información de forma anónima.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-338" data-mp="2h60kbu5nm3ja">6. Tus Derechos</h4>
        <p data-id="element-339" data-mp="2m30tidxsvmem">
          Tienes derecho a solicitar acceso, corrección o eliminación de tus
          datos personales en cualquier momento contactándonos a través de
          nuestro correo de soporte.
        </p>
      </Modal>

      {/* Contact Modal */}
      <Modal isOpen={activeModal === 'contact'} onClose={() => setActiveModal(null)} title="Contacto" data-id="element-340" data-mp="2gesgf5mutwt4">
        <p className="text-lg text-brand-text" data-id="element-341" data-mp="2bfz59jv1ihw4">
          Si tienes dudas, preguntas o necesitas soporte con tu compra, puedes
          escribirnos directamente.
        </p>
        <div className="bg-brand-beige/50 rounded-xl p-6 space-y-4 mt-4" data-id="element-342" data-mp="22dptyrry6kho">
          <div data-id="element-343" data-mp="1xg7t7oo33bkr">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-text/50 mb-1" data-id="element-344" data-mp="1xfy4s5iotch6">
              Correo electrónico
            </p>
            <p className="text-lg font-semibold text-brand-green" data-id="element-345" data-mp="1smv6jk2yteun">
              productodigitalnc@gmail.com
            </p>
          </div>
          <div data-id="element-346" data-mp="1jot6p7uyws8y">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-text/50 mb-1" data-id="element-347" data-mp="1jpb1s15w90fa">
              Horario de atención
            </p>
            <p className="text-brand-text font-medium" data-id="element-348" data-mp="1er15tocchvsm">
              Lunes a Viernes | 9:00 AM - 6:00 PM EST
            </p>
          </div>
          <div data-id="element-349" data-mp="3le8nl9repf93">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-text/50 mb-1" data-id="element-350" data-mp="3lcnqbcdpji7j">
              Tiempo estimado de respuesta
            </p>
            <p className="text-brand-text font-medium" data-id="element-351" data-mp="3vbwh5wda0gpo">24 - 48 horas hábiles</p>
          </div>
        </div>
        <p className="text-brand-text/60 text-sm mt-4" data-id="element-352" data-mp="38cbqo2hedqj">
          Estamos aquí para ayudarte. Tu satisfacción es nuestra prioridad.
        </p>
      </Modal>
    </>;
}